import sortBy from 'lodash/sortBy'
import type {ParserT} from '@freckle/parser'
import {Parser, string, record, number, boolean, stringEnum, array, nullable} from '@freckle/parser'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import type {AutomatedAudioT} from '@freckle/educator-entities/ts/common/models/automated-audio'
import {AutomatedAudios} from '@freckle/educator-entities/ts/common/models/automated-audio'
import type {ReadAloudSupportT} from '@freckle/educator-entities/ts/common/models/read-aloud-support'
import {ReadAloudSupports} from '@freckle/educator-entities/ts/common/models/read-aloud-support'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {parser as identityProviderParser} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {RosterSourceT} from '@freckle/educator-entities/ts/users/logic/roster-source'
import {RosterSources} from '@freckle/educator-entities/ts/users/logic/roster-source'
import type {StudentNameSortFieldT} from '@freckle/educator-entities/ts/users/models/teacher'
import {StudentNameSortField} from '@freckle/educator-entities/ts/users/models/teacher'

export type StudentAttrs = {
  id: number
  sisId: string | undefined | null
  firstName: string
  lastName: string
  coinsTotal: number
  coinsToday: number
  coinsSpent: number
  grade: number
  lang: LangT
  startingFreckleTextLevel: string | undefined | null
  wordStudyDiagnosticLevel: number | undefined | null
  cleverStudentId: string | undefined | null
  classLinkStudentId: string | undefined | null
  password: string
  isTeacher: boolean
  numberFactsLevelId: string | undefined | null
  source: RosterSourceT | undefined | null
  renaissanceRPIdentifier: string | undefined | null
  renaissanceManualRPIdentifier: string | undefined | null
  readAloudSupport: ReadAloudSupportT
  automatedAudio: AutomatedAudioT
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
  storeLimit: number | undefined | null
}

export const parseStudentAttrs: ParserT<StudentAttrs> = record({
  id: number(),
  sisId: nullable(string()),
  firstName: string(),
  lastName: string(),
  coinsTotal: number(),
  coinsToday: number(),
  coinsSpent: number(),
  grade: number(),
  lang: Languages.parseT(),
  startingFreckleTextLevel: nullable(string()),
  wordStudyDiagnosticLevel: nullable(number()),
  cleverStudentId: nullable(string()),
  classLinkStudentId: nullable(string()),
  password: string(),
  isTeacher: boolean(),
  numberFactsLevelId: nullable(string()),
  source: nullable(stringEnum('RosterSourceT', RosterSources.parse)),
  renaissanceRPIdentifier: nullable(string()),
  renaissanceManualRPIdentifier: nullable(string()),
  readAloudSupport: stringEnum('ReadAloudSupportT', ReadAloudSupports.parse),
  automatedAudio: stringEnum('AutomatedAudioT', AutomatedAudios.parse),
  identityProvider: nullable(identityProviderParser),
  idpManaged: boolean(),
  storeLimit: nullable(number()),
})

export const parseBaseStudent = Parser.mkRun<StudentAttrs>(parseStudentAttrs)

export const parseBaseStudents = Parser.mkRun<Array<StudentAttrs>>(array(parseStudentAttrs))

export function wasUploaded(student: StudentAttrs): boolean {
  return student.source === RosterSources.upload
}

export function coinsRemaining(student: StudentAttrs): number {
  return calcCoinsRemaining(student.coinsTotal, student.coinsSpent)
}

export function formattedName(student: Inexact<{firstName: string; lastName: string}>): string {
  return formatFullName(student.firstName, student.lastName)
}

export function formatFullName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`
}

export function getFormattedFirstName(student: StudentAttrs): string {
  return formatFirstName(student.firstName)
}

function formatFirstName(firstName: string): string {
  return `${firstName.charAt(0).toUpperCase()}${firstName.slice(1).toLowerCase()}`
}

function calcCoinsRemaining(coinsTotal: number, coinsSpent: number): number {
  return coinsTotal - coinsSpent
}

// N.B. This type uses an open record to allow resusing this function for Student and SelfStudent.
// The refined types expose their own functions, but use this function internally.
type LinkAttrsT = Inexact<{
  renaissanceRPIdentifier: string | undefined | null
  renaissanceManualRPIdentifier: string | undefined | null
}>

export function isStarLinkedGeneric({
  renaissanceRPIdentifier,
  renaissanceManualRPIdentifier,
}: LinkAttrsT): boolean {
  return (
    (renaissanceRPIdentifier !== null && renaissanceRPIdentifier !== undefined) ||
    (renaissanceManualRPIdentifier !== null && renaissanceManualRPIdentifier !== undefined)
  )
}

export function sortStudents(
  students: Array<StudentAttrs>,
  sortField: StudentNameSortFieldT,
): Array<StudentAttrs> {
  return sortBy(students, student => nameForSorting(student, sortField))
}

export function nameForSorting(
  {firstName, lastName}: {firstName: string; lastName: string},
  sortByField?: StudentNameSortFieldT | null,
): string {
  return sortByField === StudentNameSortField.FirstName
    ? `${firstName} ${lastName}`.toLowerCase()
    : `${lastName} ${firstName}`.toLowerCase()
}
